import { useState, useEffect, useRef } from 'react'
import { useAppDispatch, useAppSelector } from '../../store/hooks'
import { addControlledSubstanceEntry } from '../../store/controlledSubstancesSlice'
import type { ControlledMedication, PatientOption, PrescriptionOption } from '../../store/controlledSubstancesSlice'
import { X } from 'lucide-react'

interface AddEntryModalProps {
  open: boolean
  onClose: () => void
  medications: ControlledMedication[]
}

const transactionTypes = ['Received', 'Dispensed', 'Wasted', 'Adjustment'] as const

export function AddEntryModal({ open, onClose, medications }: AddEntryModalProps) {
  const dispatch = useAppDispatch()
  const veterinarians = useAppSelector((s) => s.settings.veterinarians)

  const [medicationId, setMedicationId] = useState('')
  const [transactionType, setTransactionType] = useState('')
  const [quantity, setQuantity] = useState('')
  const [patientId, setPatientId] = useState('')
  const [patientSearch, setPatientSearch] = useState('')
  const [patients, setPatients] = useState<PatientOption[]>([])
  const [showPatientDropdown, setShowPatientDropdown] = useState(false)
  const [prescriptionId, setPrescriptionId] = useState('')
  const [prescriptions, setPrescriptions] = useState<PrescriptionOption[]>([])
  const [performedBy, setPerformedBy] = useState('')
  const [witnessedBy, setWitnessedBy] = useState('')
  const [notes, setNotes] = useState('')
  const [errors, setErrors] = useState<string[]>([])
  const [saving, setSaving] = useState(false)

  const patientDropdownRef = useRef<HTMLDivElement>(null)

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setMedicationId('')
      setTransactionType('')
      setQuantity('')
      setPatientId('')
      setPatientSearch('')
      setPatients([])
      setShowPatientDropdown(false)
      setPrescriptionId('')
      setPrescriptions([])
      setPerformedBy('')
      setWitnessedBy('')
      setNotes('')
      setErrors([])
      setSaving(false)
    }
  }, [open])

  // Search patients when typing
  useEffect(() => {
    if (!patientSearch.trim()) {
      setPatients([])
      return
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await fetch(
          `/.netlify/functions/controlled-substances-patients?search=${encodeURIComponent(patientSearch)}`
        )
        if (res.ok) {
          const data = await res.json()
          setPatients(data)
          setShowPatientDropdown(true)
        }
      } catch {
        // ignore
      }
    }, 200)
    return () => clearTimeout(timeout)
  }, [patientSearch])

  // Fetch matching prescriptions when patient + medication selected
  useEffect(() => {
    if (!patientId || !medicationId || transactionType !== 'Dispensed') {
      setPrescriptions([])
      setPrescriptionId('')
      return
    }
    const fetchRx = async () => {
      try {
        const res = await fetch(
          `/.netlify/functions/controlled-substances-prescriptions?patient_id=${patientId}&medication_id=${medicationId}`
        )
        if (res.ok) {
          const data = await res.json()
          setPrescriptions(data)
          if (data.length === 1) {
            setPrescriptionId(data[0].id)
          }
        }
      } catch {
        // ignore
      }
    }
    fetchRx()
  }, [patientId, medicationId, transactionType])

  // Close patient dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (patientDropdownRef.current && !patientDropdownRef.current.contains(e.target as Node)) {
        setShowPatientDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const handleSelectPatient = (patient: PatientOption) => {
    setPatientId(patient.id)
    setPatientSearch(patient.name)
    setShowPatientDropdown(false)
  }

  const validate = (): string[] => {
    const errs: string[] = []
    if (!medicationId) errs.push('Medication is required')
    if (!transactionType) errs.push('Transaction Type is required')
    if (!quantity || Number(quantity) === 0) errs.push('Quantity is required and must be greater than zero')
    if (!performedBy) errs.push('Performed By is required')
    if (transactionType === 'Dispensed' && !patientId) errs.push('Patient is required for Dispensed transactions')
    if (transactionType === 'Wasted' && !witnessedBy) errs.push('Witnessed By is required for Wasted transactions')
    return errs
  }

  const handleSave = async () => {
    const errs = validate()
    if (errs.length > 0) {
      setErrors(errs)
      return
    }
    setErrors([])
    setSaving(true)

    try {
      await dispatch(
        addControlledSubstanceEntry({
          medication_id: medicationId,
          transaction_type: transactionType,
          quantity: Number(quantity),
          patient_id: patientId || undefined,
          prescription_id: prescriptionId || undefined,
          performed_by: performedBy,
          witnessed_by: witnessedBy || undefined,
          notes: notes || undefined,
        })
      ).unwrap()
      onClose()
    } catch {
      setErrors(['Failed to save entry. Please try again.'])
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div
      data-testid="add-entry-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        data-testid="add-entry-modal"
        className="bg-surface rounded-[8px] w-full max-w-lg max-sm:max-w-[calc(100%-24px)] max-h-[85vh] overflow-y-auto"
        style={{ boxShadow: 'var(--shadow-elevation-2)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-border">
          <h2 className="text-[14px] font-semibold text-text-primary">Add Entry</h2>
          <button
            data-testid="add-entry-modal-close"
            onClick={onClose}
            className="flex items-center justify-center w-7 h-7 rounded-[4px] text-text-muted hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 px-5 py-4">
          {/* Medication */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Medication <span className="text-status-error">*</span>
            </label>
            <div className="relative" data-testid="medication-dropdown-wrapper">
              <button
                type="button"
                data-testid="add-entry-medication"
                onClick={(e) => {
                  const el = e.currentTarget.nextElementSibling as HTMLElement
                  el.classList.toggle('hidden')
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer"
              >
                {medicationId
                  ? medications.find((m) => m.id === medicationId)?.name || 'Select...'
                  : 'Select medication...'}
              </button>
              <div className="hidden absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto" style={{ boxShadow: 'var(--shadow-elevation-1)' }}>
                {medications.map((med) => (
                  <button
                    key={med.id}
                    type="button"
                    data-testid={`medication-option-${med.id}`}
                    onClick={(e) => {
                      setMedicationId(med.id)
                      const dropdown = (e.currentTarget.parentElement as HTMLElement)
                      dropdown.classList.add('hidden')
                    }}
                    className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                  >
                    {med.name}
                    {med.dea_schedule && (
                      <span className="text-[11px] text-text-muted ml-1">(Schedule {med.dea_schedule})</span>
                    )}
                  </button>
                ))}
                {medications.length === 0 && (
                  <p className="px-2 py-1.5 text-[12px] text-text-muted">No controlled substances available</p>
                )}
              </div>
            </div>
          </div>

          {/* Transaction Type */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Transaction Type <span className="text-status-error">*</span>
            </label>
            <div className="relative" data-testid="transaction-type-dropdown-wrapper">
              <button
                type="button"
                data-testid="add-entry-transaction-type"
                onClick={(e) => {
                  const el = e.currentTarget.nextElementSibling as HTMLElement
                  el.classList.toggle('hidden')
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer"
              >
                {transactionType || 'Select type...'}
              </button>
              <div className="hidden absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto" style={{ boxShadow: 'var(--shadow-elevation-1)' }}>
                {transactionTypes.map((type) => (
                  <button
                    key={type}
                    type="button"
                    data-testid={`transaction-type-option-${type}`}
                    onClick={(e) => {
                      setTransactionType(type)
                      const dropdown = (e.currentTarget.parentElement as HTMLElement)
                      dropdown.classList.add('hidden')
                    }}
                    className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Quantity */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Quantity <span className="text-status-error">*</span>
            </label>
            <input
              data-testid="add-entry-quantity"
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder={transactionType === 'Adjustment' ? 'e.g., -1 or 5' : 'e.g., 10'}
              className="h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
            />
          </div>

          {/* Patient (searchable, shown for Dispensed but available for all) */}
          <div className="flex flex-col gap-1" ref={patientDropdownRef}>
            <label className="text-[11px] text-text-muted">
              Patient {transactionType === 'Dispensed' && <span className="text-status-error">*</span>}
            </label>
            <div className="relative">
              <input
                data-testid="add-entry-patient"
                type="text"
                value={patientSearch}
                onChange={(e) => {
                  setPatientSearch(e.target.value)
                  if (!e.target.value.trim()) {
                    setPatientId('')
                    setPrescriptionId('')
                  }
                }}
                onFocus={() => {
                  if (patients.length > 0) setShowPatientDropdown(true)
                }}
                placeholder="Search patient..."
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40"
              />
              {showPatientDropdown && patients.length > 0 && (
                <div
                  data-testid="patient-dropdown"
                  className="absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto"
                  style={{ boxShadow: 'var(--shadow-elevation-1)' }}
                >
                  {patients.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      data-testid={`patient-option-${p.id}`}
                      onClick={() => handleSelectPatient(p)}
                      className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                    >
                      {p.name}
                      <span className="text-[11px] text-text-muted ml-1">({p.species} - {p.owner_name})</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Rx Number (auto-linked for Dispensed) */}
          {transactionType === 'Dispensed' && prescriptions.length > 0 && (
            <div className="flex flex-col gap-1">
              <label className="text-[11px] text-text-muted">Rx Number</label>
              <div className="relative" data-testid="rx-number-dropdown-wrapper">
                <button
                  type="button"
                  data-testid="add-entry-rx-number"
                  onClick={(e) => {
                    if (prescriptions.length > 1) {
                      const el = e.currentTarget.nextElementSibling as HTMLElement
                      el.classList.toggle('hidden')
                    }
                  }}
                  className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer"
                >
                  {prescriptionId
                    ? `RX-${String(prescriptions.find((p) => p.id === prescriptionId)?.prescription_number || '').padStart(4, '0')}`
                    : 'Select Rx...'}
                </button>
                {prescriptions.length > 1 && (
                  <div className="hidden absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto" style={{ boxShadow: 'var(--shadow-elevation-1)' }}>
                    {prescriptions.map((rx) => (
                      <button
                        key={rx.id}
                        type="button"
                        data-testid={`rx-option-${rx.id}`}
                        onClick={(e) => {
                          setPrescriptionId(rx.id)
                          const dropdown = (e.currentTarget.parentElement as HTMLElement)
                          dropdown.classList.add('hidden')
                        }}
                        className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                      >
                        RX-{String(rx.prescription_number).padStart(4, '0')}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Performed By */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Performed By <span className="text-status-error">*</span>
            </label>
            <div className="relative" data-testid="performed-by-dropdown-wrapper">
              <button
                type="button"
                data-testid="add-entry-performed-by"
                onClick={(e) => {
                  const el = e.currentTarget.nextElementSibling as HTMLElement
                  el.classList.toggle('hidden')
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer"
              >
                {performedBy || 'Select staff...'}
              </button>
              <div className="hidden absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto" style={{ boxShadow: 'var(--shadow-elevation-1)' }}>
                {veterinarians.map((vet) => (
                  <button
                    key={vet.id}
                    type="button"
                    data-testid={`performed-by-option-${vet.id}`}
                    onClick={(e) => {
                      setPerformedBy(vet.name)
                      const dropdown = (e.currentTarget.parentElement as HTMLElement)
                      dropdown.classList.add('hidden')
                    }}
                    className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                  >
                    {vet.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Witnessed By */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">
              Witnessed By {transactionType === 'Wasted' && <span className="text-status-error">*</span>}
            </label>
            <div className="relative" data-testid="witnessed-by-dropdown-wrapper">
              <button
                type="button"
                data-testid="add-entry-witnessed-by"
                onClick={(e) => {
                  const el = e.currentTarget.nextElementSibling as HTMLElement
                  el.classList.toggle('hidden')
                }}
                className="w-full h-7 px-2 rounded-[4px] border border-border bg-base text-[13px] text-left text-text-primary outline-none focus:ring-2 focus:ring-accent/40 cursor-pointer"
              >
                {witnessedBy || 'Select staff...'}
              </button>
              <div className="hidden absolute z-10 top-full left-0 w-full mt-1 bg-surface border border-border rounded-[4px] max-h-40 overflow-y-auto" style={{ boxShadow: 'var(--shadow-elevation-1)' }}>
                {veterinarians.map((vet) => (
                  <button
                    key={vet.id}
                    type="button"
                    data-testid={`witnessed-by-option-${vet.id}`}
                    onClick={(e) => {
                      setWitnessedBy(vet.name)
                      const dropdown = (e.currentTarget.parentElement as HTMLElement)
                      dropdown.classList.add('hidden')
                    }}
                    className="w-full text-left px-2 py-1.5 text-[13px] text-text-primary hover:bg-hover cursor-pointer"
                  >
                    {vet.name}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Notes */}
          <div className="flex flex-col gap-1">
            <label className="text-[11px] text-text-muted">Notes</label>
            <textarea
              data-testid="add-entry-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Optional notes..."
              rows={2}
              className="px-2 py-1 rounded-[4px] border border-border bg-base text-[13px] text-text-primary outline-none focus:ring-2 focus:ring-accent/40 resize-none"
            />
          </div>

          {/* Errors */}
          {errors.length > 0 && (
            <div data-testid="add-entry-errors" className="text-[11px] text-status-error">
              {errors.map((err, i) => (
                <p key={i}>{err}</p>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-border">
          <button
            data-testid="add-entry-cancel"
            onClick={onClose}
            className="inline-flex items-center h-7 px-3 rounded-[4px] text-[13px] text-text-secondary hover:bg-hover transition-colors duration-100 cursor-pointer"
          >
            Cancel
          </button>
          <button
            data-testid="add-entry-save"
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center h-7 px-3 rounded-[4px] bg-accent text-white text-[13px] hover:bg-accent-dark transition-colors duration-100 cursor-pointer disabled:opacity-50 disabled:cursor-default"
          >
            {saving ? 'Saving...' : 'Add Entry'}
          </button>
        </div>
      </div>
    </div>
  )
}
